import pageRoutes, { PageRoute } from '../../router/pageRoutes';
import { OrganizationId } from './organizationSeoConfig';
import { getPageSeoConfig } from './pageSeoConfigs';
import { toAbsoluteUrl } from './urls';

const SiteName = 'Gaspar AI';
const SiteDescription = 'Generative AI employee help desk to auto-resolve issues.';
const SiteLanguage = 'en';

export const WebSiteId = toAbsoluteUrl('/#website');
export const SoftwareApplicationId = toAbsoluteUrl('/#software-application');

// The site itself, attributed to the company. Emitted once, site wide.
export const webSiteJsonLd = {
  '@type': 'WebSite',
  '@id': WebSiteId,
  url: toAbsoluteUrl('/'),
  name: SiteName,
  description: SiteDescription,
  publisher: { '@id': OrganizationId },
  inLanguage: SiteLanguage,
};

const knownPageRoutes = Object.values(pageRoutes) as string[];
const isKnownPageRoute = (pathname: string): pathname is PageRoute => knownPageRoutes.includes(pathname);

/**
 * A WebPage node for the current route, tied back to the site and the company. Dynamic routes are
 * skipped: /blog/[urlFriendlyName] already publishes a richer BlogPosting node from its own SEO
 * config, and a WebPage built from an unresolved route pattern would describe a page that does not
 * exist. `pathname` is the file route, so it matches the values held in pageRoutes.
 *
 * NOTE: no dateModified. These pages are built from source rather than a CMS, so the only dates
 * available are build time or file mtime, and both change on every deploy whether or not the copy
 * did. Publishing either would be a freshness signal that is simply untrue.
 */
export const getWebPageJsonLd = (pathname: string) => {
  if (!isKnownPageRoute(pathname)) {
    return null;
  }
  const { title, description } = getPageSeoConfig(pathname);
  const url = toAbsoluteUrl(pathname);
  return {
    '@type': 'WebPage',
    '@id': `${url}#webpage`,
    url,
    name: title,
    description,
    isPartOf: { '@id': WebSiteId },
    about: { '@id': OrganizationId },
    inLanguage: SiteLanguage,
  };
};

/**
 * The product itself. Prices mirror PricingCards, the only place the site publishes them: Standard
 * is $12 per user per month billed annually. Enterprise is quoted per customer, so it carries no
 * price and is not published as an Offer rather than being given an invented one. No
 * aggregateRating either, since the site holds no review data to aggregate.
 */
export const softwareApplicationJsonLd = {
  '@type': 'SoftwareApplication',
  '@id': SoftwareApplicationId,
  name: SiteName,
  description: SiteDescription,
  url: toAbsoluteUrl('/'),
  applicationCategory: 'BusinessApplication',
  applicationSubCategory: 'Help Desk Software',
  operatingSystem: 'Web-based',
  publisher: { '@id': OrganizationId },
  offers: [
    {
      '@type': 'Offer',
      name: 'Standard',
      url: toAbsoluteUrl(pageRoutes.Pricing),
      price: '12',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: '12',
        priceCurrency: 'USD',
        unitText: 'user per month, billed annually',
        referenceQuantity: { '@type': 'QuantitativeValue', value: 1, unitCode: 'MON' },
      },
    },
  ],
};

// Routes that describe the product rather than the company, the blog or a legal document.
const SoftwareApplicationRoutes: string[] = [
  pageRoutes.Index,
  pageRoutes.Pricing,
  pageRoutes.Platform,
  pageRoutes.GasparForIT,
  pageRoutes.GasparForHR,
  pageRoutes.GasparForSales,
  pageRoutes.UseCases,
  pageRoutes.HowItWorks,
  pageRoutes.Benefits,
  pageRoutes.Integrations,
];

export const getSoftwareApplicationJsonLd = (pathname: string) =>
  SoftwareApplicationRoutes.includes(pathname) ? softwareApplicationJsonLd : null;
